import { useMemo } from "react";
import { t, useI18n } from "@bookingcom/lingojs-react";
import { useAuth } from "../../../hooks/useAuth";
import { useCampaignPerformanceQuery } from "../../../hooks/useCampaignPerformanceQuery";
import { useAirlinesCampaignPerformanceQuery } from "../../../hooks/useAirlinesCampaignPerformanceQuery";
import { useSelectedAirline } from "../../../hooks/useSelectedAirline";
import { ChartNoData } from "../ChartNoData";
import { AverageDiscountChart, type AverageDiscountAirlineSeries } from "./AverageDiscountChart";

export type SingleSeriesAverageDiscountLoaderProps = {
  from: Date;
  to: Date;
};

export const SingleSeriesAverageDiscountLoader = ({ from, to }: SingleSeriesAverageDiscountLoaderProps) => {
  const i18n = useI18n();
  const { isAirlineUser } = useAuth();
  const useQuery = isAirlineUser ? useCampaignPerformanceQuery : useAirlinesCampaignPerformanceQuery;
  const { selectedAirline } = useSelectedAirline({ useSuspense: true });
  const { data: metrics } = useQuery({
    from,
    to,
    metrics: ["AVERAGE_DISCOUNT_PER_BOOKING", "AVERAGE_DISCOUNT_PERCENTAGE_PER_BOOKING"],
    airlineCode: selectedAirline?.code,
  });

  const series: AverageDiscountAirlineSeries[] = useMemo(() => {
    const euroMetric = metrics.find((metric) => metric.metadata.metricName === "AVERAGE_DISCOUNT_PER_BOOKING");
    const percentageMetric = metrics.find(
      (metric) => metric.metadata.metricName === "AVERAGE_DISCOUNT_PERCENTAGE_PER_BOOKING"
    );

    if (!euroMetric || !percentageMetric) {
      return [];
    }

    return [
      {
        airlineCode: selectedAirline?.code ?? "",
        airlineName: selectedAirline?.name ?? "",
        euroMetric,
        percentageMetric,
      },
    ];
  }, [selectedAirline, metrics]);

  if (!series.length) {
    return <ChartNoData titleText={i18n.trans(t("flights_airline_portal_realtime_performance_metric_two_name"))} />;
  }

  return <AverageDiscountChart series={series} />;
};
